import React, { useEffect, useState } from "react";
import {
  Row,
  Typography,
  Layout,
  Skeleton,
  Descriptions,
  Tag,
  Button,
  Space,
  message,
  Modal,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Get } from "../../config/api/get";
import { Patch } from "../../config/api/patch";
import { ASSOCIATE_ADMIN } from "../../config/constants";

const STATUS_COLORS = {
  PENDING: "gold",
  ACTIVE: "green",
  INACTIVE: "red",
};

function AssociateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [data, setData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadAssociate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAssociate = async () => {
    setLoading(true);
    try {
      const response = await Get(ASSOCIATE_ADMIN.get + id, token);
      if (response?.status) {
        setData(response.data);
      } else {
        message.error(response?.message || "Associate not found");
        navigate("/associate-management");
      }
    } catch (error) {
      message.error(error?.message || "Failed to load associate");
      navigate("/associate-management");
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (action) => {
    setActionLoading(true);
    try {
      const path = `${ASSOCIATE_ADMIN.approve}${id}/${action}`;
      const response = await Patch(path, token, {});
      if (response?.status) {
        message.success(response?.message || "Updated successfully");
        setConfirmAction(null);
        loadAssociate();
      } else {
        message.error(response?.message || "Action failed");
      }
    } catch (error) {
      message.error(error?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const associate = data?.associate;
  const coach = associate?.coach;

  return (
    <Layout className="configuration">
      <div className="boxDetails2" style={{ padding: "20px" }}>
        <Button
          type="link"
          icon={<FaArrowLeft />}
          onClick={() => navigate("/associate-management")}
          style={{ paddingLeft: 0, marginBottom: 16 }}
        >
          Back to associates
        </Button>

        {loading ? (
          <Skeleton active />
        ) : associate ? (
          <>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 20 }}
            >
              <Typography.Title level={3} style={{ margin: 0 }}>
                {associate.firstName} {associate.lastName}
              </Typography.Title>
              <Space>
                {associate.status === "PENDING" ? (
                  <>
                    <Button
                      className="loginButton"
                      loading={actionLoading}
                      style={{ minWidth: 120, height: 40 }}
                      onClick={() => setConfirmAction("approve")}
                    >
                      Approve
                    </Button>
                    <Button
                      className="loginButton2"
                      loading={actionLoading}
                      style={{ minWidth: 120, height: 40 }}
                      onClick={() => setConfirmAction("decline")}
                    >
                      Decline
                    </Button>
                  </>
                ) : (
                  <Button
                    className="loginButton"
                    loading={actionLoading}
                    style={{ minWidth: 140, height: 40 }}
                    onClick={() => setConfirmAction("toggle")}
                  >
                    {associate.status === "ACTIVE" ? "Deactivate" : "Activate"}
                  </Button>
                )}
              </Space>
            </Row>

            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Email">{associate.email}</Descriptions.Item>
              <Descriptions.Item label="Position">
                {associate.position}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[associate.status]}>
                  {associate.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Registered">
                {associate.createdAt
                  ? dayjs(associate.createdAt).format("MM/DD/YYYY HH:mm")
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Trainer" span={2}>
                {coach
                  ? `${coach.firstName} ${coach.lastName} (${coach.email})`
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Trainer status">
                {coach?.status || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Courses assigned">
                {data?.courseAccessCount ?? 0}
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : null}
      </div>

      <Modal
        open={Boolean(confirmAction)}
        className="StyledModal"
        title={
          confirmAction === "approve"
            ? "Approve associate"
            : confirmAction === "decline"
            ? "Decline associate"
            : associate?.status === "ACTIVE"
            ? "Deactivate associate"
            : "Activate associate"
        }
        onOk={() => runAction(confirmAction)}
        onCancel={() => setConfirmAction(null)}
        confirmLoading={actionLoading}
        okText="Yes"
        cancelText="No"
        cancelButtonProps={{
          style: {
            border: "2px solid #2484c6",
            color: "#2484c6",
            height: "auto",
            padding: "6px 35px",
            borderRadius: "50px",
            fontSize: "16px",
          },
        }}
        okButtonProps={{
          style: {
            backgroundColor: "#2484c6",
            color: "white",
            height: "auto",
            padding: "5px 35px",
            borderRadius: "50px",
            fontSize: "16px",
            border: "2px solid #2484c6",
          },
        }}
      >
        {confirmAction === "approve"
          ? "Approve this associate and email login credentials?"
          : confirmAction === "decline"
          ? "Decline this associate request?"
          : associate?.status === "ACTIVE"
          ? "Deactivate this associate?"
          : "Activate this associate?"}
      </Modal>
    </Layout>
  );
}

export default AssociateDetails;
